import React, { useEffect } from "react";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";
import { ReviewRow } from "./ReviewRow";
import { formatIsoDate } from "../config";
import { mailingAddressCounty, trackerCity } from "../mailingAddress";

/**
 * Sponsor-copy print mode: adds the body class the print CSS keys off, fires
 * the print dialog, and clears itself when the dialog closes.
 *
 * Lifted out of Step6Review in Round 21 so the confirmation screen drives the
 * identical behaviour rather than a second, subtly different copy of it. The
 * caller owns the boolean; this owns what happens while it is true.
 *
 * Callers MUST render <SponsorCopy> INSTEAD OF their normal content while
 * `active` — the print CSS hides the page chrome, but only not rendering the
 * sensitive content keeps it out of the printed DOM entirely.
 */
export function useSponsorPrintMode(active: boolean, onDone: () => void): void {
  useEffect(() => {
    if (!active) return;
    document.body.classList.add("sponsor-print");
    const done = () => onDone();
    window.addEventListener("afterprint", done);
    const t = window.setTimeout(() => window.print(), 60);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("afterprint", done);
      document.body.classList.remove("sponsor-print");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * Round 12 — the sponsor copy. Renders from an explicit ALLOW-LIST only:
 * program name + session dates, applicant full name, home city/county, high
 * school, graduation year, the Step 1 attestation, application status, and
 * the sponsor section. Nothing else from the form exists in this render — no
 * DOB, contact info, family/emergency contacts, medical, insurance, uploads,
 * or signatures.
 *
 * Round 21 moved this out of Step6Review.tsx unchanged so the post-submission
 * confirmation screen can render the same component. The footer's Reference
 * Number has been here since Round 12; it reads live from meta, which is why
 * it prints "— (not yet submitted)" before submission and the real number
 * after — including on a magic-link return visit and after an amendment,
 * which preserves the number rather than reissuing it.
 *
 * `backLabel` is the ONLY thing the two callers differ on: Step 6 returns to
 * the review, the confirmation screen returns to the confirmation.
 */
export function SponsorCopy({
  onBack,
  backLabel = "← Back to review",
}: {
  onBack: () => void;
  backLabel?: string;
}) {
  const { data } = useApplication();
  const cfg = usePublicConfig();
  const s2 = data.step2;
  const fullName = [s2.legalFirstName, s2.legalMiddleName, s2.legalLastName, s2.suffix]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(" ");
  const statusLabel =
    data.meta.status === "InProgress"
      ? "In progress"
      : data.meta.status === "AmendmentInProgress"
        ? "Amendment in progress"
        : data.meta.status;
  // Local calendar date (formatIsoDate is UTC-pinned, which could shift the
  // printed date around midnight).
  const printedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

  return (
    <section className="sponsor-copy space-y-6">
      <div className="no-print flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" onClick={onBack}>
          {backLabel}
        </button>
        <button type="button" className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="section-card border-2 border-slate-500">
        <p className="text-sm font-semibold mb-1">Sponsor Copy</p>
        <p className="text-sm text-slate-700">
          All required application information, including health, insurance, and emergency-contact
          information, has been collected by the program. It is not shown on this copy in order to
          protect the applicant's privacy. This copy contains no medical, insurance, contact, or
          date-of-birth information.
        </p>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">{cfg.programName}</h2>
        <p className="text-sm text-slate-600 mb-3">
          Session: {formatIsoDate(cfg.sessionStartDate)} – {formatIsoDate(cfg.sessionEndDate)}
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Applicant" value={fullName || "—"} />
          <ReviewRow label="City" value={trackerCity(s2) || "—"} />
          <ReviewRow label="County" value={mailingAddressCounty(s2) || "—"} />
          <ReviewRow label="High school" value={s2.highSchoolName || "—"} />
          <ReviewRow
            label="Graduation year"
            value={s2.graduationYear != null ? String(s2.graduationYear) : "—"}
          />
          <ReviewRow
            label="Full-session / sponsor attestation"
            value={data.step1.attestationAcknowledged ? "✓ Acknowledged" : "✗ Not acknowledged"}
          />
          <ReviewRow label="Application status" value={statusLabel} />
        </dl>
      </div>

      <div className="section-card">
        <h2 className="text-lg font-semibold mb-2">Sponsor</h2>
        {data.sponsor.sponsorConfirmed ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <ReviewRow label="Sponsor confirmed" value="Yes" />
            <ReviewRow
              label="Post / Unit"
              value={
                `${data.sponsor.sponsorPostName}${data.sponsor.sponsorPostNumber ? ` #${data.sponsor.sponsorPostNumber}` : ""}`.trim() ||
                "—"
              }
            />
            <ReviewRow label="Confirmed date" value={data.sponsor.sponsorConfirmedDate || "—"} />
          </dl>
        ) : (
          <p className="text-sm text-slate-900">Sponsor not yet secured.</p>
        )}
      </div>

      <div className="section-card text-sm text-slate-700 space-y-1">
        <p>
          Reference Number:{" "}
          {data.meta.referenceNumber ? data.meta.referenceNumber : "— (not yet submitted)"}
        </p>
        <p>Printed: {printedDate}</p>
        <p>
          To verify this application or to sponsor this applicant, contact {cfg.programName} at{" "}
          {cfg.supportEmail}.
        </p>
      </div>
    </section>
  );
}
